import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Heart, Trash2, CheckCircle2, BookOpen, Play, Dumbbell, MessageCircle, Sparkles, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEL_EMOJI = ['😖', '😕', '🙂', '😊', '🤩'];

export default function RunCoach() {
  const [tab, setTab] = useState('chat');
  return (
    <div className="max-w-4xl mx-auto" data-testid="run-coach">
      <div className="neo-card p-4 mb-4 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF] flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-3xl border-4 border-black flex-shrink-0">
          🏃‍♀️
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-700">Löparcoach</div>
          <div className="font-['Fredoka'] text-2xl font-bold leading-tight">Ester</div>
          <div className="text-xs opacity-70">Snäll, ADHD-medveten. Vi bygger kondis mjukt — utan press 💗</div>
        </div>
      </div>

      <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="run-coach-tabs">
        {[
          { id: 'chat',     label: 'Chatta',    emoji: '💬', color: 'bg-[#FFC8DD]' },
          { id: 'plan',     label: 'Vecko-plan', emoji: '🗓️', color: 'bg-[#C8B6FF]' },
          { id: 'logs',     label: 'Loggbok',   emoji: '📔', color: 'bg-[#A2D2FF]' },
          { id: 'lessons',  label: 'Lektioner', emoji: '📚', color: 'bg-[#B9FBC0]' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1.5 transition ${
              tab === t.id ? `${t.color} scale-[1.03]` : 'bg-white opacity-75 hover:opacity-100'
            }`}
            data-testid={`run-tab-${t.id}`}
          >
            <span>{t.emoji}</span> {t.label}
          </button>
        ))}
      </div>

      {tab === 'chat' && <EsterChat />}
      {tab === 'plan' && <PlanView />}
      {tab === 'logs' && <LogsView />}
      {tab === 'lessons' && <LessonsView />}
    </div>
  );
}

// ============== CHAT ==============
function EsterChat() {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hej älskling 🌸 Jag är Ester. Det är helt okej att bara vara nyfiken — vi behöver inte springa idag. Berätta var du står, så tar vi det därifrån 💗' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/run-coach/chat`, { session_id: sessionId, message: text }, { withCredentials: true });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch {
      toast.error('Ester kunde inte svara just nu');
    } finally { setLoading(false); }
  };

  return (
    <div className="neo-card bg-white p-3 flex flex-col" style={{ minHeight: '460px' }} data-testid="ester-chat">
      <div className="flex-1 overflow-y-auto space-y-2 pr-1" style={{ maxHeight: '480px' }}>
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
              m.role === 'user' ? 'bg-[#FFC8DD] text-black' : 'bg-[#F5EEFF] border-2 border-black/10'
            }`}>
              {m.role === 'assistant' && <div className="text-[10px] uppercase font-bold opacity-60 mb-0.5">Ester 🏃‍♀️</div>}
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div className="text-xs opacity-60">Ester tänker… 💭</div>}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-1.5 mt-2 pt-2 border-t border-black/10">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Fråga Ester något (t.ex. hur börjar jag?)…"
          className="flex-1 neo-input px-3 py-2 text-sm"
          disabled={loading}
          data-testid="ester-input"
        />
        <button onClick={send} disabled={loading || !input.trim()} className="neo-btn px-3 py-2 bg-[#FFC8DD] font-bold disabled:opacity-50" data-testid="ester-send-btn">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============== PLAN ==============
function PlanView() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWeek, setOpenWeek] = useState(null);

  useEffect(() => {
    axios.get(`${API}/run-coach/plan`, { withCredentials: true })
      .then((r) => { setPlan(r.data); setOpenWeek(r.data.current_week); })
      .catch(() => toast.error('Kunde inte ladda plan'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center opacity-60 py-10">Laddar planen… 🏃‍♀️</div>;
  if (!plan) return null;

  return (
    <div className="space-y-3" data-testid="plan-view">
      <div className="neo-card p-3 bg-[#FFF5D1] flex items-center gap-2 text-sm">
        <Award className="w-5 h-5" />
        <div>
          Nivå: <strong className="capitalize">{plan.level}</strong> · Klara pass: <strong>{plan.completed_sessions}</strong> · Aktuell vecka: <strong>{plan.current_week}</strong>
        </div>
      </div>
      {plan.weeks.map((w, wi) => {
        const isOpen = openWeek === w.week;
        const isCurrent = plan.current_week === w.week;
        return (
          <div key={w.week} className={`neo-card ${isCurrent ? 'bg-[#FFE8F5]' : 'bg-white'}`} data-testid={`plan-week-${w.week}`}>
            <button
              onClick={() => setOpenWeek(isOpen ? null : w.week)}
              className="w-full flex items-center justify-between p-3 text-left"
            >
              <div>
                <div className="text-[10px] uppercase font-bold opacity-60">Vecka {w.week} {isCurrent && '· NU'}</div>
                <div className="font-['Fredoka'] font-bold text-lg">{w.focus}</div>
              </div>
              <div className="text-2xl">{isOpen ? '▾' : '▸'}</div>
            </button>
            {isOpen && (
              <div className="px-3 pb-3 space-y-2">
                {w.sessions.map((s, si) => (
                  <div key={si} className="neo-card p-2.5 bg-white flex items-start gap-2" data-testid={`plan-session-${w.week}-${si}`}>
                    <Play className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs font-bold">{s.label} · {s.minutes} min</div>
                      <div className="text-sm">{s.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============== LOGS ==============
function LogsView() {
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ distance_km: '', duration_min: '', feel_1_5: 3, notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/run-coach/logs`, { withCredentials: true })
      .then((r) => setLogs(r.data.logs || []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        distance_km: form.distance_km ? Number(form.distance_km) : null,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        feel_1_5: Number(form.feel_1_5),
        notes: form.notes || null,
      };
      const r = await axios.post(`${API}/run-coach/logs`, payload, { withCredentials: true });
      toast.success(`Sparat 🏃‍♀️ +${r.data.happiness_delta} happiness, +${r.data.micro_win_delta} micro-vinst 💗`);
      setForm({ distance_km: '', duration_min: '', feel_1_5: 3, notes: '' });
      load();
    } catch {
      toast.error('Kunde inte spara');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort denna logg?')) return;
    try {
      await axios.delete(`${API}/run-coach/logs/${id}`, { withCredentials: true });
      toast.success('Loggen är borta');
      load();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  return (
    <div className="space-y-3" data-testid="logs-view">
      <div className="neo-card p-3 bg-[#E8F0FF] space-y-2">
        <div className="text-xs font-bold uppercase">Nytt löppass</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            Distans (km)
            <input type="number" step="0.1" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} className="w-full neo-input p-1.5 text-sm mt-0.5" data-testid="log-distance" />
          </label>
          <label className="text-xs">
            Tid (min)
            <input type="number" step="1" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="w-full neo-input p-1.5 text-sm mt-0.5" data-testid="log-duration" />
          </label>
        </div>
        <div>
          <div className="text-xs mb-1">Hur kändes det?</div>
          <div className="flex gap-1.5" data-testid="log-feel-row">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setForm({ ...form, feel_1_5: n })}
                className={`neo-btn px-2 py-1 text-lg transition ${form.feel_1_5 === n ? 'bg-[#FFC8DD] scale-110' : 'bg-white'}`}
                data-testid={`log-feel-${n}`}
              >
                {FEEL_EMOJI[n - 1]}
              </button>
            ))}
          </div>
        </div>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Frivilliga noter (hur kroppen kändes, väder…)"
          rows={2}
          className="w-full neo-input p-1.5 text-sm resize-none"
          data-testid="log-notes"
        />
        <button onClick={save} disabled={saving} className="w-full neo-btn py-2 bg-[#FFC8DD] font-bold text-sm disabled:opacity-50" data-testid="log-save-btn">
          {saving ? 'Sparar…' : 'Spara passet 💗'}
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="neo-card p-6 text-center bg-white/60 text-sm opacity-70" data-testid="logs-empty">
          Inga pass loggade ännu. Bara att komma ut och gå räknas 🌸
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((l) => (
            <div key={l.id} className="neo-card p-2.5 bg-white flex items-center gap-2" data-testid={`log-${l.id}`}>
              <div className="text-2xl">{FEEL_EMOJI[(l.feel_1_5 || 3) - 1]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs opacity-70">{l.date}</div>
                <div className="text-sm">
                  {l.duration_min ? `${l.duration_min} min` : '—'}
                  {l.distance_km ? ` · ${l.distance_km} km` : ''}
                </div>
                {l.notes && <div className="text-[11px] opacity-70 italic truncate">"{l.notes}"</div>}
              </div>
              <button onClick={() => remove(l.id)} className="text-black/50 hover:text-red-500 p-1" data-testid={`log-delete-${l.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============== LESSONS ==============
function LessonsView() {
  const [lessons, setLessons] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/run-coach/lessons`, { withCredentials: true })
      .then((r) => setLessons(r.data.lessons || []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openLesson = async (L) => {
    setOpenId(L.id);
    if (!L.read) {
      try {
        await axios.post(`${API}/run-coach/lessons/${L.id}/read`, {}, { withCredentials: true });
        load();
      } catch {}
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="lessons-view">
      {lessons.map((L) => (
        <div
          key={L.id}
          onClick={() => openLesson(L)}
          className={`neo-card p-3 cursor-pointer hover:scale-[1.02] transition ${L.read ? 'bg-[#F5F5F5]' : 'bg-[#FFF5D1]'}`}
          data-testid={`lesson-${L.id}`}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-2xl">{L.emoji}</div>
            {L.read && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </div>
          <div className="font-['Fredoka'] font-bold text-sm">{L.title}</div>
          {openId === L.id && (
            <div className="mt-2 text-xs opacity-80 whitespace-pre-wrap">{L.body}</div>
          )}
        </div>
      ))}
    </div>
  );
}
